/**
 * Bahasa: Indonesia / English, bisa ditukar kapan saja.
 *
 * TANPA PUSTAKA. Dua bahasa, kamus datar, dan satu fungsi `t()` — itu cukup, dan
 * pustaka i18n membawa pemuatan malas, pluralisasi, dan format pesan yang tidak
 * satu pun dibutuhkan sekarang. Kalau suatu saat butuh (bahasa ketiga dengan
 * aturan jamak yang berbeda), penggantinya menyentuh berkas ini saja.
 *
 * ATURAN YANG MEMBUATNYA TETAP MURAH: tidak ada string tampilan yang ditulis
 * langsung di komponen. Semuanya lewat `t()`. Memasang i18n belakangan berarti
 * menyentuh tiap berkas; memasangnya di awal berarti tidak menyentuh apa pun lagi.
 *
 * YANG TIDAK DITERJEMAHKAN, DAN ALASANNYA:
 *
 *   1. Pesan galat dari server. Backend punya 148 `HTTPException` dan NOL di
 *      antaranya membawa kode — semuanya prosa Bahasa Indonesia. Menerjemahkannya
 *      di sini berarti mencocokkan kalimat, dan kalimat itu berubah tanpa ada yang
 *      memberi tahu frontend. Jadi galat server ditampilkan APA ADANYA, dan itu
 *      terlihat sebagai bahasa campur saat UI berbahasa Inggris. Itu pilihan sadar;
 *      perbaikannya ada di backend (kode galat), bukan di sini.
 *   2. Nama teknis: nama tabel, nama peran, nama model, `chat_id`. Menerjemahkan
 *      `tenant_admin` jadi "admin penyewa" membuat layar dan database bicara dua
 *      bahasa tentang satu hal.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import id from '../locales/id.json'
import en from '../locales/en.json'

const KAMUS = { id, en }
const BAHASA_SAH = Object.keys(KAMUS)
const KUNCI_SIMPAN = 'smova.bahasa'

const Konteks = createContext(null)

function bahasaAwal() {
  try {
    const tersimpan = localStorage.getItem(KUNCI_SIMPAN)
    if (tersimpan && BAHASA_SAH.includes(tersimpan)) return tersimpan
  } catch {
    // Mode penyamaran / penyimpanan diblokir. Bukan galat — cuma tidak ada pilihan tersimpan.
  }
  // `id` sebagai bawaan, bukan bahasa peramban: penggunanya tim Indonesia, dan
  // menebak dari peramban membuat orang yang sama melihat bahasa berbeda di dua laptop.
  return 'id'
}

export function PenyediaBahasa({ children }) {
  const [bahasa, setBahasaState] = useState(bahasaAwal)

  useEffect(() => {
    document.documentElement.lang = bahasa
    try {
      localStorage.setItem(KUNCI_SIMPAN, bahasa)
    } catch {
      /* diabaikan dengan sengaja — lihat bahasaAwal() */
    }
  }, [bahasa])

  const t = useCallback(
    (kunci, isian) => {
      // Cadangan berlapis: bahasa aktif -> Indonesia -> kuncinya sendiri.
      // Kunci yang muncul di layar adalah cara paling cepat menemukan terjemahan
      // yang belum ditulis; menggantinya dengan string kosong menyembunyikannya.
      let teks = KAMUS[bahasa]?.[kunci] ?? KAMUS.id?.[kunci] ?? kunci
      if (isian) {
        for (const [nama, nilai] of Object.entries(isian)) {
          // KURUNG GANDA DULU, baru tunggal — dan urutannya yang membuatnya benar.
          //
          // Fungsi ini dulu hanya mengganti `{nama}`, sementara 24 dari 34 kunci
          // berisian ditulis `{{nama}}`. Hasilnya kurung luarnya tertinggal: "{{n}} orang"
          // jadi "{4} orang". Ia muncul di layar sebagai teks berkurung yang aneh, tidak
          // pernah melempar galat, dan lolos setiap pemeriksa yang ada — build hijau,
          // kunci i18n ada, komponen merender.
          //
          // Kalau tunggal diganti lebih dulu, `{{n}}` jadi `{4}` dan kurung sisanya tidak
          // pernah hilang. Ganda dulu membuat kedua bentuk selesai bersih.
          teks = teks.replaceAll(`{{${nama}}}`, String(nilai))
          teks = teks.replaceAll(`{${nama}}`, String(nilai))
        }
      }
      return teks
    },
    [bahasa],
  )

  const nilai = useMemo(
    () => ({
      bahasa,
      setBahasa: (b) => BAHASA_SAH.includes(b) && setBahasaState(b),
      bahasaTersedia: BAHASA_SAH,
      t,
      // Locale untuk Intl — dipakai format tanggal dan angka.
      locale: bahasa === 'en' ? 'en-US' : 'id-ID',
    }),
    [bahasa, t],
  )

  return <Konteks.Provider value={nilai}>{children}</Konteks.Provider>
}

export function useBahasa() {
  const k = useContext(Konteks)
  if (!k) throw new Error('useBahasa dipakai di luar <PenyediaBahasa>')
  return k
}
